import { defineStore } from 'pinia';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

export const useCommunitiesStore = defineStore('communities', {
  state: () => ({
    communities: [],
    loading: false,
    lastFetchTime: null,
    error: null,
    searchQuery: '',
  }),

  getters: {
    // Vérifie si le cache est encore valide
    isCacheValid: (state) => {
      if (!state.lastFetchTime || state.communities.length === 0) {
        return false;
      }
      return Date.now() - state.lastFetchTime < CACHE_DURATION;
    },

    // Retourne les communautés triées par nombre de membres
    communitiesByMembers: (state) => {
      return [...state.communities].sort((a, b) => b.members - a.members);
    },

    // Retourne les communautés triées par nom (A-Z)
    communitiesByNameAZ: (state) => {
      return [...state.communities].sort((a, b) =>
        a.issuerProfile.name.localeCompare(b.issuerProfile.name)
      );
    },

    // Retourne les communautés triées par nom (Z-A)
    communitiesByNameZA: (state) => {
      return [...state.communities].sort((a, b) =>
        b.issuerProfile.name.localeCompare(a.issuerProfile.name)
      );
    },
  },

  actions: {
    // Récupère les détails d'un issuer
    async fetchIssuerDetails(did, api, utils) {
      let issuerProfile;
      let memberCount;
      let templateData;
      let createdAt;

      await Promise.all([
        new Promise(async (resolve, reject) => {
          let profileIpfsUrl;
          try {
            const { data } = await api.getIssuerDetailsLinkFromCode(did);
            profileIpfsUrl = data.service[0]?.serviceEndpoint;
          } catch (e) {
            reject(`Error fetching did document for DID ${did}:`, e);
          }
          if (!profileIpfsUrl) {
            console.log('No profile on IPFS for issuer :', did);
            resolve();
          }

          try {
            issuerProfile = await utils.getJsonDataFromUrl(profileIpfsUrl);
          } catch (e) {
            reject(`Error fetching profile for DID ${did}:`, e);
          }

          resolve();
        }),
        new Promise(async (resolve, reject) => {
          let templateList;
          const addr = utils.didToAddress(did);
          try {
            templateList = await utils.getTemplateList(addr, 'Membership');
          } catch (e) {
            reject(`Error retrieving templates for DID ${did}:`, e);
          }

          createdAt = templateList[0].status?.timestamp
            ? Number(templateList[0].status.timestamp)
            : null;
          const membercardTemplateHash = templateList[0].templateHash;
          try {
            templateData = await utils.getTemplateData(membercardTemplateHash);

            try {
              memberCount = await api.getCommunityMemberCount(
                membercardTemplateHash.split('0x')[1]
              );
              memberCount = memberCount.data.members.number;
            } catch (e) {
              memberCount = 0;
            }
          } catch (e) {
            reject(`Error retrieving membercard data for ${did}:`, e);
          }

          resolve();
        }),
      ]).catch((e) => {
        console.error('Error fetching issuer details:', e);
        throw e;
      });

      return {
        did,
        issuerProfile,
        members: memberCount,
        templateData,
        createdAt,
      };
    },

    // Récupère toutes les communautés
    async fetchCommunities(api, utils, forceRefresh = false) {
      // Si le cache est valide et qu'on ne force pas le refresh, on utilise le cache
      if (this.isCacheValid && !forceRefresh) {
        return this.communities;
      }

      this.loading = true;
      this.error = null;

      try {
        const issuersDids = (await api.getFeaturedCommunities()).data;
        const fetchedCommunities = await Promise.all(
          issuersDids.map((did) => this.fetchIssuerDetails(did, api, utils))
        );

        this.communities = fetchedCommunities.filter(
          (issuer) => issuer.issuerProfile
        );
        this.lastFetchTime = Date.now();

        // Sauvegarde dans localStorage pour persistance entre sessions
        this.saveToLocalStorage();
      } catch (e) {
        this.error = e.message || 'Error fetching communities';
        console.error('Error fetching communities:', e);
      } finally {
        this.loading = false;
      }

      return this.communities;
    },

    // Sauvegarde le cache dans localStorage
    saveToLocalStorage() {
      try {
        const cacheData = {
          communities: this.communities,
          lastFetchTime: this.lastFetchTime,
        };
        localStorage.setItem('communities_cache', JSON.stringify(cacheData));
      } catch (e) {
        console.warn('Failed to save communities to localStorage:', e);
      }
    },

    // Charge le cache depuis localStorage
    loadFromLocalStorage() {
      try {
        const cached = localStorage.getItem('communities_cache');
        if (cached) {
          const { communities, lastFetchTime } = JSON.parse(cached);
          this.communities = communities || [];
          this.lastFetchTime = lastFetchTime || null;
        }
      } catch (e) {
        console.warn('Failed to load communities from localStorage:', e);
      }
    },

    // Efface le cache
    clearCache() {
      this.communities = [];
      this.lastFetchTime = null;
      localStorage.removeItem('communities_cache');
    },
  },
});
